import is from '@sindresorhus/is';
import { useContext } from 'react';
import { createSearchParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';

import { globalSearchTermSchema } from '../../data-contract/global-search';
import { route } from '../pages/global-search-page/constants';

export type TabParam = 'employees' | 'jobLevels' | 'jobRoles' | 'jobs' | 'learningOpportunities' | 'skills';
const tabParamValues = ['skills', 'employees', 'jobs', 'learningOpportunities', 'jobRoles', 'jobLevels'];

const isValidSearchParam = (value: string | null): value is TabParam =>
    typeof value === 'string' && tabParamValues.includes(value);

const { searchTermParam, skillIdsParam, tabParam } = route.searchParams;

type RedirectQueryParams = {
    readonly [tabParam]: TabParam;
    readonly [searchTermParam]: string;
    readonly [skillIdsParam]: string;
};

const idSchema = z.uuid();
const SkillIdsSchema = idSchema.array().nonempty();

type GlobalSearchTableParams = {
    readonly getSearchParamsForTab: (tab: TabParam) => URLSearchParams;
    readonly isValidSearchTerm: boolean;
    readonly isValidSkillIdsList: boolean;
    readonly redirectTo: (queryParams: Partial<RedirectQueryParams>) => void;
    readonly redirectToSearchTerm: (searchTerm: string, tab?: TabParam) => void;
    readonly redirectToSkillIds: (skillsIds: readonly string[], tab?: TabParam) => void;
    readonly searchTermResult: string;
    readonly setSearchTermResult: (value: string) => void;
    readonly skillIds: readonly string[];
    readonly tableTabParam: TabParam;
    readonly updateSkillIds: (skillIds: ReadonlyArray<string>) => void;
};

export const useGlobalSearchTableParams = (): GlobalSearchTableParams => {
    const { actions } = useContext(ApplicationContext);
    const { search: searchPath } = route.patterns;
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParamValue = searchParams.get(tabParam);
    const tableTabParam = isValidSearchParam(tabParamValue) ? tabParamValue : 'skills';
    const searchTermResult = searchParams.get(searchTermParam) ?? '';
    const skillsIdsValue = searchParams.get(skillIdsParam) ?? '';

    const skillIds: ReadonlyArray<string> = is.emptyString(skillsIdsValue) ? [] : skillsIdsValue.split(',');

    const isValidSkillIdsList = SkillIdsSchema.safeParse(skillIds).success;
    const isValidSearchTerm = globalSearchTermSchema.safeParse(searchTermResult).success;

    const setSearchTermResult = (value: string): void => {
        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.set(searchTermParam, value);
        updatedSearchParams.delete('page');
        updatedSearchParams.delete('pageSize');
        setSearchParams(updatedSearchParams);
    };

    const redirectTo = (queryParams: Partial<RedirectQueryParams>): void => {
        actions.navigateTo({
            pathname: searchPath,
            search: createSearchParams({
                ...queryParams,
            }).toString(),
        });
    };

    const getSearchParamsForTab = (tab: TabParam): URLSearchParams => {
        const search = new URLSearchParams(searchParams);
        search.set(tabParam, tab);
        search.delete('page');
        search.delete('pageSize');

        return search;
    };

    const updateSkillIds = (skillIds: ReadonlyArray<string>): void => {
        const updatedSearchParams = new URLSearchParams(searchParams);

        const updatedSkillIds = skillIds.join(',');

        if (is.emptyArray(skillIds)) {
            updatedSearchParams.delete(skillIdsParam);
        } else {
            updatedSearchParams.set(skillIdsParam, updatedSkillIds);
        }

        setSearchParams(updatedSearchParams);
    };

    const redirectToSearchTerm = (searchTerm: string, tab?: TabParam): void => {
        const isValidSearchQuery = globalSearchTermSchema.safeParse(searchTerm).success;

        if (!isValidSearchQuery) {
            return;
        }

        const searchTermOptions: Omit<RedirectQueryParams, 'skillIdsParam'> = {
            searchTerm,
            tab: tab ?? 'skills',
        };

        const redirectOptions = Object.fromEntries(
            Object.entries(searchTermOptions).filter(([, value]) => is.string(value)),
        );

        redirectTo({
            ...redirectOptions,
        });
    };

    const redirectToSkillIds = (skillsIds: readonly string[], tab?: TabParam): void => {
        const flatSkillIds = skillsIds.join(',');

        const isValidSkillIds = SkillIdsSchema.safeParse(skillsIds).success;

        if (!isValidSkillIds) {
            return;
        }

        const searchTermOptions: Omit<RedirectQueryParams, 'searchTerm'> = {
            skillIdsParam: flatSkillIds,
            tab: tab ?? 'skills',
        };

        const redirectOptions = Object.fromEntries(
            Object.entries(searchTermOptions).filter(([, value]) => is.string(value)),
        );

        redirectTo({
            ...redirectOptions,
        });
    };

    return {
        getSearchParamsForTab,
        isValidSearchTerm,
        isValidSkillIdsList,
        redirectTo,
        redirectToSearchTerm,
        redirectToSkillIds,
        searchTermResult,
        setSearchTermResult,
        skillIds,
        tableTabParam,
        updateSkillIds,
    };
};

export const defaultPagination = { count: 0, page: 0, pages: 0, size: 0 };
